import { ErrorState, LoadingBlock } from '../components/states';
import { PlanStatusChip } from '../components/chips';
import { usePlanDetail } from '../api/queries';
import { formatDate, formatKg, formatPercent } from '../format';
import { t } from '../i18n/pt-BR';
import { usePlan } from '../plan/PlanContext';

type PlaceholderKey = 'load' | 'schedule' | 'orders' | 'materials' | 'master' | 'import' | 'plans';

/**
 * Tela ainda não construída (marcos M1–M4). Mostra o plano selecionado e seu resumo — dado real vindo
 * do BFF — para provar a ligação ponta a ponta. Não calcula nada.
 */
export function PlaceholderPage({ screen }: { screen: PlaceholderKey }) {
  const info = t.placeholder[screen];
  const { selected, isLoading, error, refetch } = usePlan();
  const detail = usePlanDetail(selected?.['nr-plano']);

  return (
    <>
      <section className="panel">
        <h2>{info.title}</h2>
        <p className="hint">
          {t.placeholder.milestone(info.milestone)} {t.placeholder.wiredHint}
        </p>
      </section>

      {isLoading || detail.isLoading ? <LoadingBlock /> : null}
      {error ? <ErrorState error={error} onRetry={refetch} /> : null}
      {detail.error ? <ErrorState error={detail.error} onRetry={() => void detail.refetch()} /> : null}

      {detail.data ? (
        <section aria-label={t.plan.summaryLabel}>
          <p className="hint" style={{ marginBottom: 10 }}>
            #{detail.data['nr-plano']} · {detail.data.descricao} · {formatDate(detail.data['dt-ini'])} a{' '}
            {formatDate(detail.data['dt-fim'])} <PlanStatusChip situacao={detail.data.situacao} />
          </p>
          <div className="kpis">
            <Kpi
              label={t.plan.kpi.orders}
              value={detail.data.resumo.pedidos}
              sub={`${formatKg(detail.data.resumo['total-kg'])} ${t.plan.kpi.totalKg}`}
            />
            <Kpi label={t.plan.kpi.onTime} value={detail.data.resumo['no-prazo']} tone="ok" />
            <Kpi label={t.plan.kpi.atRisk} value={detail.data.resumo['em-risco']} tone="warn" />
            <Kpi label={t.plan.kpi.late} value={detail.data.resumo.atrasados} tone="bad" />
            <Kpi
              label={t.plan.kpi.bottleneck}
              value={detail.data.resumo['maquina-gargalo']}
              sub={formatPercent(detail.data.resumo['pct-carga-gargalo'])}
            />
          </div>
        </section>
      ) : null}
    </>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'ok' | 'warn' | 'bad';
}) {
  return (
    <div className={`kpi ${tone ?? ''}`}>
      <div className="l">{label}</div>
      <div className={`v ${tone ?? ''}`}>{value}</div>
      {sub ? <div className="s">{sub}</div> : null}
    </div>
  );
}
